<template>
  <main class="monitor smoke-monitor">
    <tianditu-map
      ref="map"
      class="smoke-map"
      :smoke-buildings="mapBuildings"
      @smoke-click="selectPoint"
      @map-ready="handleMapReady"
      @map-error="mapError = true"
    />

    <header class="monitor-header smoke-header">
      <div class="smoke-brand">
        <span class="smoke-eyebrow">白鹤洞街道 · 消防物联</span>
        <h1>无线烟感监管</h1>
      </div>
      <button
        type="button"
        class="smoke-mode-return"
        @click="returnToMonitor"
      >
        切换回单位模式
      </button>
    </header>

    <section
      class="monitor-top smoke-overview"
      aria-label="当前筛选范围统计"
    >
      <div class="smoke-scope">
        <span>当前范围</span>
        <strong>{{ hasFilter ? '筛选结果' : '白鹤洞街道' }}</strong>
      </div>
      <div
        v-for="item in metrics"
        :key="item.label"
        class="smoke-metric"
      >
        <span>{{ item.label }}</span>
        <strong>{{ item.value }}</strong>
      </div>
    </section>

    <aside
      class="monitor_wrap smoke-panel smoke-list-panel"
      aria-label="安装点位"
    >
      <div class="smoke-panel-heading">
        <h2>安装点位</h2>
        <span>{{ filteredDevices.length }}</span>
      </div>
      <label class="smoke-search">
        <i
          class="el-icon-search"
          aria-hidden="true"
        />
        <input
          v-model.trim="keyword"
          type="search"
          placeholder="搜索社区、地址、设备编号"
          aria-label="搜索安装点位"
        >
      </label>
      <div class="smoke-filter-row">
        <label for="smoke-status">设备状态</label>
        <select
          id="smoke-status"
          v-model="statusFilter"
        >
          <option value="">
            全部状态
          </option>
          <option
            v-for="status in availableStatuses"
            :key="status"
            :value="status"
          >
            {{ status }}
          </option>
        </select>
        <button
          v-if="hasFilter"
          type="button"
          class="smoke-text-button"
          @click="clearFilters"
        >
          清除
        </button>
      </div>
      <div class="smoke-point-list">
        <template v-if="filteredDevices.length">
          <section
            v-for="group in communityGroups"
            :key="group.name"
          >
            <div class="smoke-building-row">
              <span><strong>{{ group.name }}</strong><small>{{ group.devices.length }} 台烟感</small></span>
            </div>
            <button
              v-for="device in group.devices"
              :key="device.id"
              type="button"
              class="smoke-point"
              :class="{ 'is-selected': selectedDeviceId === device.id }"
              :aria-pressed="selectedDeviceId === device.id"
              @click="selectDevice(device)"
            >
              <span class="smoke-point-top"><strong>{{ device.room }}<small>房</small></strong><span class="smoke-status">{{ device.status }}</span></span>
              <span class="smoke-point-location">{{ device.location }}</span>
              <span class="smoke-point-bottom"><span>{{ device.locationPending ? '位置待核实' : '无线烟感 · 1' }}</span><span>查看详情 <i
                class="el-icon-arrow-right"
                aria-hidden="true"
              /></span></span>
            </button>
          </section>
        </template>
        <div
          v-else
          class="smoke-empty"
        >
          <i
            class="el-icon-search"
            aria-hidden="true"
          />
          <strong>未找到安装点位</strong>
          <p>请调整房号、地址或设备状态。</p>
          <button
            type="button"
            class="smoke-text-button"
            @click="clearFilters"
          >
            清除筛选
          </button>
        </div>
      </div>
    </aside>

    <aside
      class="monitor_wrap smoke-panel smoke-detail-panel"
      aria-label="设备与点位详情"
    >
      <div class="smoke-panel-heading">
        <h2>{{ selectedDevice ? '点位详情' : '范围概况' }}</h2>
        <button
          v-if="selectedDevice"
          type="button"
          class="smoke-text-button"
          @click="closeDetail"
        >
          收起详情
        </button>
        <span v-else>当前范围</span>
      </div>
      <div class="smoke-detail-scroll">
        <p class="smoke-snapshot-note">
          设备状态为资料快照，非实时监测数据。
        </p>
        <template v-if="selectedDevice">
          <div class="smoke-device-heading">
            <img
              src="/monitor/images/bdmap/marker_smoke_building.svg"
              alt="烟感监管楼宇"
              width="64"
              height="80"
            >
            <div><span>{{ selectedDevice.community }}</span><h3>{{ selectedDevice.room }} 房<span class="smoke-status">{{ selectedDevice.status }}</span></h3></div>
          </div>
          <p class="smoke-location">
            <i
              class="el-icon-location-outline"
              aria-hidden="true"
            />{{ selectedDevice.location }}
          </p>
          <section class="smoke-detail-section">
            <h3>设备信息</h3>
            <dl class="smoke-fields">
              <div><dt>部件名称</dt><dd>{{ selectedDevice.name }}</dd></div>
              <div><dt>部件类型</dt><dd>{{ selectedDevice.type }}</dd></div>
              <div v-if="selectedDevice.system">
                <dt>所属系统</dt><dd>{{ selectedDevice.system }}</dd>
              </div>
              <div v-if="selectedDevice.manufacturer">
                <dt>设施生产单位</dt><dd>{{ selectedDevice.manufacturer }}</dd>
              </div>
              <div v-if="selectedDevice.unitName">
                <dt>所属单位</dt><dd>{{ selectedDevice.unitName }}</dd>
              </div>
              <div><dt>所属场所</dt><dd>{{ selectedDevice.place }}</dd></div>
              <div v-if="selectedDevice.locationPending">
                <dt>位置说明</dt><dd>位置待核实，暂不落图</dd>
              </div>
              <div v-if="selectedDevice.displayPosition">
                <dt>位置说明</dt><dd>区域内示意位置，非实际安装位置；原始坐标保留。</dd>
              </div>
              <template v-if="selectedDevice.serialNumber">
                <div><dt>设备序列号</dt><dd>{{ selectedDevice.serialNumber }}</dd></div>
                <div><dt>IMEI</dt><dd>{{ selectedDevice.imei }}</dd></div>
                <div><dt>设备型号</dt><dd>{{ selectedDevice.model }}</dd></div>
                <div><dt>接入方式</dt><dd>{{ selectedDevice.connection }}</dd></div>
                <div><dt>在线状态</dt><dd>{{ selectedDevice.onlineStatus }}</dd></div>
                <div><dt>电池电量</dt><dd>{{ selectedDevice.battery }}%</dd></div>
                <div><dt>信号强度</dt><dd>{{ selectedDevice.signal }}</dd></div>
                <div><dt>布撤防</dt><dd>{{ selectedDevice.arming }}</dd></div>
                <div><dt>首次上线</dt><dd>{{ selectedDevice.firstOnlineAt }}</dd></div>
                <div><dt>最后上线</dt><dd>{{ selectedDevice.lastOnlineAt }}</dd></div>
                <div><dt>安装时间</dt><dd>{{ selectedDevice.installedAt }}</dd></div>
              </template>
            </dl>
          </section>
          <section class="smoke-detail-section">
            <div class="smoke-section-heading">
              <h3>负责人</h3>
              <button
                type="button"
                class="smoke-text-button"
                :aria-pressed="showPhones"
                @click="showPhones = !showPhones"
              >
                {{ showPhones ? '隐藏号码' : '显示号码' }}
              </button>
            </div>
            <div
              v-for="contact in selectedDevice.contacts"
              :key="contact.role"
              class="smoke-contact"
            >
              <span>{{ contact.role }}</span><strong>{{ contact.name }}</strong><span class="smoke-phone">{{ showPhones ? contact.phone : maskedPhone(contact.phone) }}</span>
            </div>
          </section>
        </template>
        <template v-else-if="filteredDevices.length">
          <div class="smoke-building-heading">
            <span>{{ selectedPointId ? '当前点位' : '已登记范围' }}</span><h3>白鹤洞街道</h3>
            <p>{{ selectedPointId ? '同坐标设备分别保留，请从左侧选择设备。' : '按社区查看设备，按地理位置查看分布。' }}</p>
          </div>
          <dl class="smoke-fields">
            <div><dt>烟感设备</dt><dd>{{ filteredDevices.length }} 台</dd></div>
            <div><dt>地图点位</dt><dd>{{ visiblePointCount }} 个</dd></div>
            <div><dt>位置待核实</dt><dd>{{ filteredDevices.filter(device => device.locationPending).length }} 台</dd></div>
          </dl>
          <section class="smoke-detail-section">
            <h3>设备状态分布</h3>
            <div
              v-for="item in statusCounts"
              :key="item.name"
              class="smoke-state-row"
            >
              <span>{{ item.name }}</span><div class="smoke-state-track">
                <span :style="{ width: (item.count / filteredDevices.length * 100) + '%' }" />
              </div><strong>{{ item.count }}</strong>
            </div>
          </section>
        </template>
        <div
          v-else
          class="smoke-empty"
        >
          <strong>当前范围暂无设备</strong><p>清除筛选后查看设备与安装点位。</p>
        </div>
      </div>
    </aside>

    <div class="monitor-bfooter smoke-map-toolbar">
      <div class="smoke-map-context">
        <span class="smoke-map-dot" /><span>{{ selectedPointId ? '已选烟感点位' : '白鹤洞街道 · 烟感分布' }}</span>
      </div>
      <button
        type="button"
        :disabled="!mapReady"
        @click="resetOverview"
      >
        <i
          class="el-icon-full-screen"
          aria-hidden="true"
        />街道总览
      </button>
    </div>
    <p
      v-if="mapError"
      class="smoke-map-message"
      role="status"
    >
      地图部分内容加载失败，仍可在两侧查看设备资料。
    </p>
    <p
      v-else-if="!mapReady"
      class="smoke-map-message"
      role="status"
    >
      正在加载街道地图…
    </p>
  </main>
</template>

<script>
import TiandituMap from '../components/TiandituMap.vue';
import { smokeDevices, getSmokePoints } from './smokeData';

export default {
  name: 'SmokeMonitor',
  components: { TiandituMap },
  data () {
    return {
      devices: smokeDevices,
      keyword: '',
      statusFilter: '',
      selectedDeviceId: null,
      selectedPointId: null,
      showPhones: false,
      mapReady: false,
      mapError: false
    };
  },
  computed: {
    hasFilter () { return !!(this.keyword || this.statusFilter || this.selectedPointId); },
    availableStatuses () { return [...new Set(this.devices.map(device => device.status))]; },
    // 搜索和状态筛选决定地图范围；点位选择仅收窄列表，保留其他地图入口。
    matchingDevices () {
      return this.devices.filter(device => {
        const text = [device.community, device.place, device.room, device.location, device.name,
          device.serialNumber, device.imei].join(' ');
        return text.includes(this.keyword) && (!this.statusFilter || device.status === this.statusFilter);
      });
    },
    // 列表、当前范围数量和状态统计共用同一筛选结果，不混入单位监控统计。
    filteredDevices () {
      return this.matchingDevices.filter(device => !this.selectedPointId || device.pointId === this.selectedPointId);
    },
    communityGroups () {
      const groups = new Map();
      this.filteredDevices.forEach(device => {
        if (!groups.has(device.community)) groups.set(device.community, { name: device.community, devices: [] });
        groups.get(device.community).devices.push(device);
      });
      return [...groups.values()];
    },
    mapBuildings () { return getSmokePoints(this.matchingDevices); },
    visiblePointCount () { return getSmokePoints(this.filteredDevices).length; },
    selectedDevice () { return this.filteredDevices.find(device => device.id === this.selectedDeviceId); },
    statusCounts () {
      return this.availableStatuses.map(name => ({ name, count: this.filteredDevices.filter(device => device.status === name).length }));
    },
    metrics () {
      return [
        { label: '地图点位', value: this.visiblePointCount },
        { label: '无线烟感', value: this.filteredDevices.length }
      ];
    }
  },
  methods: {
    // 返回时移除烟感选择参数，保留原单位监控页的查询条件。
    returnToMonitor () {
      const query = { ...this.$route.query };
      delete query.smokePoint;
      this.$router.push({ name: 'monitor', query });
    },
    // 直接进入展示街道总览；从主图进入时选中对应位置，不默认定位原公寓。
    handleMapReady () {
      this.mapReady = true;
      // 先计算与主图一致的总览缩放门槛，再定位所选点位。
      this.$refs.map.resetSmokeOverview();
      if (this.selectedDevice) this.selectDevice(this.selectedDevice);
      else if (this.selectedPointId) this.selectPoint(this.selectedPointId);
    },
    maskedPhone (phone) { return `${phone.slice(0, 3)}****${phone.slice(-4)}`; },
    clearFilters () {
      this.keyword = '';
      this.statusFilter = '';
      this.resetOverview();
    },
    // 共点只表示相同资料坐标；多设备交由列表分别选择，不认定为同栋楼。
    selectPoint (pointId) {
      const point = this.mapBuildings.find(item => item.id === pointId);
      if (!point) return;
      this.selectedPointId = pointId;
      this.closeDetail();
      if (this.mapReady) this.$refs.map.focusSmokeBuilding(point);
      if (this.filteredDevices.length === 1) this.selectedDeviceId = this.filteredDevices[0].id;
    },
    // 列表选择不收窄社区范围；待核实设备只展示资料，不使用异常坐标移动地图。
    selectDevice (device) {
      const point = this.mapBuildings.find(item => item.id === device.pointId);
      if (this.mapReady && point) this.$refs.map.focusSmokeBuilding(point);
      if (this.mapReady && device.locationPending) this.$refs.map.resetSmokeOverview();
      this.selectedDeviceId = device.id;
      this.showPhones = false;
    },
    closeDetail () { this.selectedDeviceId = null; this.showPhones = false; },
    resetOverview () {
      this.closeDetail();
      this.selectedPointId = null;
      if (this.mapReady) this.$refs.map.resetSmokeOverview();
    }
  },
  watch: {
    matchingDevices () {
      if (!this.matchingDevices.some(device => device.pointId === this.selectedPointId)) this.selectedPointId = null;
    },
    filteredDevices () {
      if (!this.selectedDevice) this.closeDetail();
    }
  },
  created () {
    // 先恢复入口选择，地图加载失败时仍能查看所选点位资料。
    this.selectPoint(this.$route.query.smokePoint);
  }
};
</script>

<style lang="scss" scoped src="./smoke.scss" />
